// Cadre de l'application : barre latérale (modules repliables) + zone de contenu.
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Badge, Box, Collapse, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  SpaceDashboardOutlined, AddShoppingCartOutlined, Inventory2Outlined, AssignmentOutlined, HourglassBottomOutlined, QrCode2Outlined, HistoryOutlined,
  AddBoxOutlined, LocalShippingOutlined, CalendarMonthOutlined, FactCheckOutlined, ManageHistoryOutlined,
  ReceiptLongOutlined, StorefrontOutlined, GroupOutlined, SettingsOutlined, KeyOutlined, ConstructionOutlined,
  BadgeOutlined, EventNoteOutlined, EventBusyOutlined, BeachAccessOutlined, PeopleAltOutlined, TuneOutlined,
  ShoppingCartOutlined, StoreMallDirectoryOutlined, AccountBalanceOutlined, RequestQuoteOutlined, PaymentsOutlined,
  WorkOutlineOutlined, LogoutOutlined, ExpandMore, FactoryOutlined, WarehouseOutlined, Groups2Outlined,
  CalculateOutlined, AdminPanelSettingsOutlined, DescriptionOutlined, ForumOutlined, CarRepairOutlined, GridViewOutlined, ViewModuleOutlined, AssignmentTurnedInOutlined, Menu as MenuIcon,
  DateRangeOutlined, DomainOutlined, ManageSearchOutlined,
  MonitorHeartOutlined, InsightsOutlined, AccountBalanceWalletOutlined, PriceCheckOutlined, VerifiedOutlined, InventoryOutlined, ChecklistOutlined, ViewWeekOutlined, EngineeringOutlined, EventAvailableOutlined, EditCalendarOutlined, PointOfSaleOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import NotificationBell from './NotificationBell';
import useChatUnread from '../hooks/useChatUnread';
import { useAuth } from '../context/AuthContext';
import EtatConnexion from './EtatConnexion';
import { tokens } from '../theme';

const WIDTH = 256;
const ROLE_LABEL = {
  admin: 'Administrateur', sous_admin: 'Responsable', employee: 'Employé', rh: 'Responsable RH', comptable: 'Comptable', achats: 'Responsable achats', vigile: 'Vigile',
  briques: 'Équipe briques & pavés', resp_briques: 'Responsable briques & pavés',
};

/**
 * Modules du menu. Les créations (nouvelle facture, nouvelle commande…) se font
 * depuis le bouton de chaque liste ; seul le nouveau BL reste au menu des équipes terrain.
 */
function useSections() {
  const { isAdmin, isMetier, isComptable, isAchats, canViewDashboard, canManageUsers, canManageRh, canCompta, canAchats, canValiderFactures, canSuivrePannes, canMarches, canBriques, canGererBriques, canHebdo, canAmortissement, canPaie, canNumeroter, canDirection, canGarde, canDepenses, hasRole } = useAuth();
  const terrain = !isMetier; // production, stock, livraisons : hors rôles métier
  return useMemo(() => [
    {
      id: 'accueil',
      items: [
        { label: 'Tableau de bord', path: '/dashboard', icon: SpaceDashboardOutlined, show: canViewDashboard },
        { label: 'Poste de garde', path: '/garde', icon: ShieldOutlined, show: canGarde },
        { label: 'Tableau de bord direction', path: '/direction', icon: InsightsOutlined, show: canDirection },
        { label: 'Rapport hebdomadaire', path: '/rapport-hebdo', icon: DateRangeOutlined, show: canHebdo },
        // Ouvert à tous les utilisateurs
        { label: 'Messages', path: '/messages', icon: ForumOutlined, show: true },
      ],
    },
    {
      id: 'planification',
      title: 'Planification',
      icon: EditCalendarOutlined,
      items: [
        { label: 'Tâches', path: '/planification/taches', icon: ChecklistOutlined, show: true },
        { label: 'Planning de production', path: '/planification/production', icon: ViewWeekOutlined, show: terrain },
        { label: 'Maintenance préventive', path: '/planification/maintenance', icon: EngineeringOutlined, show: true },
        { label: 'Planning des livraisons', path: '/planification/livraisons', icon: EventAvailableOutlined, show: terrain },
        { label: 'Planning du personnel', path: '/planification/personnel', icon: Groups2Outlined, show: isAdmin || hasRole('sous_admin') || canManageRh },
        { label: 'Planning des intrants', path: '/planification/intrants', icon: InventoryOutlined, show: terrain || isAchats },
      ],
    },
    {
      id: 'production',
      title: 'Production',
      icon: FactoryOutlined,
      items: [
        { label: 'Rapport journalier', path: '/daily-report', icon: AssignmentOutlined, show: terrain && !isAdmin },
        { label: 'Maturation', path: '/pba-maturation', icon: HourglassBottomOutlined, show: canViewDashboard },
        { label: 'Rapports de production', path: '/reports-history', icon: HistoryOutlined, show: canViewDashboard },
        { label: 'Identifiants poteaux', path: '/poteaux', icon: QrCode2Outlined, show: terrain || canNumeroter },
        { label: 'Contrôle qualité', path: '/qualite', icon: VerifiedOutlined, show: terrain },
        { label: 'Système qualité (ISO)', path: '/qualite-systeme', icon: ChecklistOutlined, show: terrain || canViewDashboard },
        { label: 'Coût de revient', path: '/cout-revient', icon: PriceCheckOutlined, show: isAdmin || hasRole('sous_admin') || (isComptable && canPaie) },
        { label: 'Pannes', path: '/pannes', icon: CarRepairOutlined, show: canSuivrePannes },
      ],
    },
    {
      id: 'briques',
      title: 'Briques & pavés',
      icon: ViewModuleOutlined,
      items: [
        { label: 'Rapport journalier', path: '/briques/rapport', icon: AssignmentOutlined, show: hasRole('briques') || hasRole('resp_briques') },
        { label: 'Rapports de production', path: '/briques/rapports', icon: HistoryOutlined, show: canBriques },
        { label: 'Articles', path: '/briques/articles', icon: GridViewOutlined, show: canGererBriques },
      ],
    },
    {
      id: 'stock',
      title: 'Stock',
      icon: WarehouseOutlined,
      items: [
        { label: 'Stock PBA', path: '/stock', icon: Inventory2Outlined, show: terrain },
        { label: 'Stock intrants', path: '/stock-intrants', icon: ConstructionOutlined, show: terrain || isAchats },
        { label: "Stock d'armatures", path: '/stock-armatures', icon: ViewModuleOutlined, show: terrain },
        { label: 'Inventaire mensuel', path: '/monthly-inventory', icon: CalendarMonthOutlined, show: canViewDashboard },
        { label: 'Inventaire physique', path: '/physical-inventory', icon: FactCheckOutlined, show: canViewDashboard },
        { label: 'Historique inventaires', path: '/inventory-history', icon: ManageHistoryOutlined, show: canViewDashboard },
      ],
    },
    {
      id: 'livraisons',
      title: 'Livraisons',
      icon: LocalShippingOutlined,
      items: [
        { label: 'Marchés clients (BC, ADL)', path: '/marches', icon: AssignmentTurnedInOutlined, show: canMarches },
        { label: 'Nouveau bon de livraison', path: '/pba-delivery', icon: AddBoxOutlined, show: terrain && !isAdmin },
        { label: 'Bons de livraison', path: '/bons-livraison', icon: LocalShippingOutlined, show: terrain },
        { label: 'Journal du poste de garde', path: '/garde/journal', icon: ShieldOutlined, show: isAdmin || hasRole('sous_admin') },
      ],
    },
    {
      id: 'rh',
      title: 'Ressources humaines',
      icon: Groups2Outlined,
      items: [
        { label: 'Pointage du jour', path: '/rh', icon: BadgeOutlined, show: canManageRh },
        { label: 'Tableau de présence', path: '/rh/presence', icon: EventNoteOutlined, show: canManageRh },
        { label: 'Absences', path: '/rh/absences', icon: EventBusyOutlined, show: canManageRh },
        { label: 'Congés', path: '/rh/conges', icon: BeachAccessOutlined, show: canManageRh },
        { label: 'Employés', path: '/rh/employes', icon: PeopleAltOutlined, show: canManageRh },
        { label: 'Paramètres RH', path: '/rh/parametres', icon: TuneOutlined, show: canManageRh },
      ],
    },
    {
      id: 'compta',
      title: 'Comptabilité',
      icon: CalculateOutlined,
      items: [
        { label: 'Situation', path: '/compta', icon: AccountBalanceOutlined, show: canCompta },
        { label: 'Créances clients', path: '/compta/creances', icon: AccountBalanceWalletOutlined, show: canCompta },
        { label: 'Factures clients', path: '/factures', icon: ReceiptLongOutlined, show: canCompta || canValiderFactures },
        { label: 'Proformas (devis)', path: '/proformas', icon: DescriptionOutlined, show: canCompta || canValiderFactures },
        { label: 'BL à facturer', path: '/bons-livraison', icon: LocalShippingOutlined, show: isComptable && isMetier },
        { label: 'Clients', path: '/clients', icon: StorefrontOutlined, show: canCompta },
        { label: 'Factures fournisseurs', path: '/compta/factures-fournisseurs', icon: RequestQuoteOutlined, show: canCompta },
        { label: 'Paie du mois', path: '/compta/paie', icon: PaymentsOutlined, show: canPaie },
        { label: 'Salaires', path: '/compta/salaires', icon: WorkOutlineOutlined, show: canPaie },
        { label: 'Immobilisations & amortissements', path: '/amortissements', icon: DomainOutlined, show: canAmortissement },
      ],
    },
    {
      id: 'achats',
      title: 'Achats',
      icon: ShoppingCartOutlined,
      items: [
        { label: "Demandes d'achat", path: '/demandes-achat', icon: AddShoppingCartOutlined, show: true },
        { label: 'Bons de commande', path: '/achats', icon: ShoppingCartOutlined, show: canAchats || isComptable },
        { label: 'Fournisseurs', path: '/achats/fournisseurs', icon: StoreMallDirectoryOutlined, show: canAchats || isComptable },
      ],
    },
    {
      id: 'situation-depenses',
      title: 'Situation des Dépenses',
      icon: AccountBalanceOutlined,
      items: [
        { label: 'Dépenses (situation des charges)', path: '/depenses', icon: ReceiptLongOutlined, show: canDepenses },
        { label: 'Suivi de caisse', path: '/caisse', icon: PointOfSaleOutlined, show: canDepenses },
      ],
    },
    {
      id: 'admin',
      title: 'Administration',
      icon: AdminPanelSettingsOutlined,
      items: [
        { label: 'Utilisateurs', path: '/users', icon: GroupOutlined, show: canManageUsers },
        { label: 'Paramètres société', path: '/parametres', icon: SettingsOutlined, show: canManageUsers },
        { label: 'Journal des actions', path: '/journal', icon: ManageSearchOutlined, show: isAdmin },
        { label: 'État du système', path: '/systeme', icon: MonitorHeartOutlined, show: isAdmin },
      ],
    },
  ].map((s) => ({ ...s, items: s.items.filter((i) => i.show) })).filter((s) => s.items.length),
  [isAdmin, isMetier, isComptable, isAchats, canViewDashboard, canManageUsers, canManageRh, canCompta, canAchats, canValiderFactures, canSuivrePannes, canMarches, canBriques, canGererBriques, canHebdo, canAmortissement, canPaie, canNumeroter, canDirection, canGarde, canDepenses, hasRole, terrain]);
}

/** L'élément actif est celui dont le chemin correspond le plus précisément à l'adresse. */
function activePath(sections, pathname) {
  const all = sections.flatMap((s) => s.items.map((i) => i.path));
  return all
    .filter((p) => pathname === p || pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
}

const initials = (name = '') => name.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

function NavItem({ label, path, icon: Icon, active, onClick, nested, badge = 0 }) {
  return (
    <ListItemButton
      onClick={() => onClick(path)}
      aria-current={active ? 'page' : undefined}
      sx={{
        borderRadius: 1.5, mb: 0.25, py: 0.55, pl: nested ? 2.25 : 1.5, position: 'relative',
        color: active ? '#fff' : 'rgba(255,255,255,.78)',
        bgcolor: active ? 'rgba(255,255,255,.10)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(255,255,255,.07)', color: '#fff' },
        '&::before': active ? {
          content: '""', position: 'absolute', left: -12, top: 6, bottom: 6, width: 4,
          borderRadius: '0 4px 4px 0', bgcolor: tokens.amber,
        } : {},
      }}
    >
      <ListItemIcon sx={{ minWidth: 32, color: active ? tokens.amber : 'inherit' }}><Icon sx={{ fontSize: 19 }} /></ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }} />
      {badge > 0 && (
        <Box aria-label={`${badge} non lu(s)`} sx={{ minWidth: 20, height: 20, px: 0.75, borderRadius: 10, bgcolor: '#2EAD5B', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
          {badge > 99 ? '99+' : badge}
        </Box>
      )}
    </ListItemButton>
  );
}

function Sidebar({ onNavigate }) {
  const sections = useSections();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, roles, logout, home } = useAuth();
  const nonLus = useChatUnread();
  const badgeOf = (path) => (path === '/messages' ? nonLus : 0);
  const current = activePath(sections, location.pathname);
  const currentSection = sections.find((s) => s.items.some((i) => i.path === current))?.id;
  // Menu court (rôles métier, employés) : tout reste déplié
  const collapsible = sections.reduce((n, s) => n + s.items.length, 0) > 14;
  // Un seul module ouvert à la fois : celui de la page affichée, ou celui qu'on vient de déplier
  const [openId, setOpenId] = useState(currentSection || null);
  useEffect(() => { if (currentSection) setOpenId(currentSection); }, [currentSection]);
  const toggle = (id) => setOpenId((o) => (o === id ? null : id));
  const go = (path) => { navigate(path); onNavigate?.(); };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: tokens.navy, color: '#fff' }}>
      <Box
        component="button" onClick={() => go(home)}
        sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, pt: 2.5, pb: 2 }}
      >
        <Box component="img" src="/logo-mark-white.png" alt="" sx={{ height: 40 }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: '0.12em', lineHeight: 1 }}>DOCC</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 0.5 }}>Poteaux béton armé</Typography>
        </Box>
      </Box>

      <Box component="nav" aria-label="Menu principal" sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {sections.map((section) => {
          // Section sans titre, ou ne contenant qu'une entrée pour cet utilisateur (menu replié) : entrée affichée directement
          if (!section.title || (collapsible && section.items.length === 1)) {
            return (
              <List key={section.id} disablePadding sx={{ mb: 0.5 }}>
                {section.items.map((it) => <NavItem key={it.path} {...it} badge={badgeOf(it.path)} active={current === it.path} onClick={go} />)}
              </List>
            );
          }
          const isOpen = !collapsible || openId === section.id;
          const SectionIcon = section.icon;
          const hasActive = currentSection === section.id;
          return (
            <Box key={section.id} sx={{ mt: collapsible ? 0.25 : 1 }}>
              {collapsible ? (
                <ListItemButton
                  onClick={() => toggle(section.id)}
                  aria-expanded={isOpen}
                  sx={{
                    borderRadius: 1.5, py: 0.6, pl: 1.5, pr: 1,
                    color: hasActive ? '#fff' : 'rgba(255,255,255,.86)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,.07)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: hasActive ? tokens.amber : 'rgba(255,255,255,.6)' }}><SectionIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                  <ListItemText primary={section.title} primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
                  <ExpandMore sx={{ fontSize: 18, opacity: 0.6, transition: 'transform .15s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                </ListItemButton>
              ) : (
                <Typography sx={{ px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>{section.title}</Typography>
              )}
              <Collapse in={isOpen} timeout={150} unmountOnExit>
                <List disablePadding sx={{ pb: collapsible ? 0.5 : 0 }}>
                  {section.items.map((it) => (
                    <NavItem key={it.path} {...it} badge={badgeOf(it.path)} nested={collapsible} active={current === it.path} onClick={go} />
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ borderTop: '1px solid rgba(255,255,255,.12)', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: tokens.navySoft, fontSize: 14, fontWeight: 600 }}>
          {initials(user?.full_name || user?.username)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{user?.full_name || user?.username}</Typography>
          <Typography noWrap sx={{ fontSize: 12, opacity: 0.65 }} title={roles.map((r) => ROLE_LABEL[r] || r).join(' · ')}>{roles.map((r) => ROLE_LABEL[r] || r).join(' · ')}</Typography>
        </Box>
        <NotificationBell color="rgba(255,255,255,.85)" onNavigate={onNavigate} />
        <Tooltip title="Changer mon mot de passe">
          <IconButton size="small" onClick={() => go('/change-password')} sx={{ color: 'rgba(255,255,255,.78)' }} aria-label="Changer mon mot de passe">
            <KeyOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Se déconnecter">
          <IconButton size="small" onClick={() => { logout(); navigate('/login'); }} sx={{ color: 'rgba(255,255,255,.78)' }} aria-label="Se déconnecter">
            <LogoutOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const AppShell = () => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const nonLus = useChatUnread();

  // Pas de cadre tant que la session n'est pas établie (la route protégée redirige)
  if (!user) return <Outlet />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {desktop ? (
        <Box component="aside" sx={{ width: WIDTH, flexShrink: 0, position: 'sticky', top: 0, height: '100vh' }}>
          <Sidebar />
        </Box>
      ) : (
        <Drawer open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: WIDTH, border: 0 } }}>
          <Sidebar onNavigate={() => setOpen(false)} />
        </Drawer>
      )}

      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {!desktop && (
          <Box className="no-print" sx={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, bgcolor: tokens.navy, color: '#fff', '@media print': { display: 'none' } }}>
            <IconButton onClick={() => setOpen(true)} sx={{ color: '#fff' }} aria-label="Ouvrir le menu"><MenuIcon /></IconButton>
            <Box component="img" src="/logo-mark-white.png" alt="" sx={{ height: 28 }} />
            <Typography sx={{ fontWeight: 700, letterSpacing: '0.12em', flex: 1 }}>DOCC</Typography>
            <Tooltip title="Messages">
              <IconButton size="small" sx={{ color: '#fff' }} onClick={() => navigate('/messages')} aria-label={`Messages${nonLus ? ` (${nonLus} non lus)` : ''}`}>
                <Badge badgeContent={nonLus} max={99} sx={{ '& .MuiBadge-badge': { bgcolor: '#2EAD5B', color: '#fff' } }}><ForumOutlined fontSize="small" /></Badge>
              </IconButton>
            </Tooltip>
            <NotificationBell color="#fff" down />
          </Box>
        )}
        <Box sx={{ px: { xs: 1.5, sm: 3, lg: 4 }, py: { xs: 2, sm: 3 }, maxWidth: 1480, mx: 'auto' }}>
          <EtatConnexion />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
